import { decode } from '@googlemaps/polyline-codec';
import { useLoading, useMoment } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useDebounceValue } from 'usehooks-ts';
import { useStyles } from '../../../hooks/useStyles';
import { getEventTimeline } from '../../../services/gptService';
import {
  eventFilterState,
  eventHoveredState,
  filterTimelineEventsState,
  timelineEventsState,
} from '../../../storage/gpt';
import { EventTimeLine, GptEventTimeLine, TimeLineType } from '../../../types/gpt';
import { useCamera } from '../../map/hooks/useCamera';
import { EVENT_ALARM, EVENT_ALARM_TYPE_LIST, EVENT_ENGINE_OFF } from '../../../constants/gpt';
import { useGptEventAlarmTypes } from './useGptEventAlarmTypes';

export function useGptTimeLineDetail() {
  const { eventId } = useParams();
  const { theme } = useStyles();
  const [events, setEvents] = useRecoilState(timelineEventsState);
  const [hoverPath, setHoverPath] = useRecoilState(eventHoveredState);
  const [selectedHoverPath, setSelectedHoverPath] = useDebounceValue<string | undefined>(hoverPath, 300);
  const [eventTypeSelected, setEventTypeSelected] = useRecoilState(eventFilterState);
  const eventsTimeline = useRecoilValue(filterTimelineEventsState);
  const resetEventTypeSelected = useResetRecoilState(eventFilterState);
  const [update, setUpdate] = useState(0);
  const { startLoading, stopLoading, loading } = useLoading();
  const { getAlarmTypeName, getAlarmColor, loadedAlarmTypes } = useGptEventAlarmTypes();
  const { formatDateFromTs, formatTimeFromTs } = useMoment();
  const { zoomTo } = useCamera();

  const [debounceHoverPoint, setDebounceHoverPoint] = useDebounceValue<GeoJSON.Point | undefined>(undefined, 300);

  useEffect(() => {
    if (debounceHoverPoint) {
      const point = debounceHoverPoint.coordinates;
      zoomTo({
        lng: point[0],
        lat: point[1],
      });
    }
  }, [debounceHoverPoint]);

  useEffect(() => {
    setHoverPath(selectedHoverPath);
  }, [selectedHoverPath]);

  const getTimeLineByEvents = (timelineEvents: EventTimeLine[]) => {
    const timeline = timelineEvents.map((e, eventIndex): GptEventTimeLine => {
      if (e.timelineType === EVENT_ALARM) {
        return {
          uuid: e.uuid,
          timelineType: e.timelineType,
          startTime: e.startTime,
          startLabel: `${e.alarmType ? getAlarmTypeName(e.alarmType) : ''}
              - ${e.speed ?? 0} km/h ${formatDateFromTs(e.startTime)}`,
          startPoint: e.startPoint,
          onHover: (isHover: boolean) => {
            setDebounceHoverPoint(isHover ? e.startPoint : undefined);
          },
          iconName: 'warning',
          speed: e.speed,
          backgroundColor: e.alarmType ? getAlarmColor(e.alarmType) : undefined,
        };
      } else if (e.timelineType === EVENT_ENGINE_OFF) {
        return {
          uuid: e.uuid,
          timelineType: e.timelineType,
          startTime: e.startTime,
          endTime: e.endTime,
          startLabel: `Inicio: ${formatTimeFromTs(e.startTime)}`,
          endLabel: `Fin: ${e.endTime ? formatTimeFromTs(e.endTime) : '-'}`,
          startPoint: e.startPoint,
          onHover: (isHover: boolean) => {
            setDebounceHoverPoint(isHover ? e.startPoint : undefined);
          },
          iconName: 'power_settings_new',
          duration: e.duration,
          startReference: e.startReference,
          backgroundColor: theme.colors.negative200,
        };
      }
      return {
        uuid: e.uuid,
        timelineType: e.timelineType,
        startTime: e.startTime,
        endTime: e.endTime,
        startLabel: `${formatTimeFromTs(e.startTime)} ${e.startReference ? ' - ' + e.startReference : ''}`,
        endLabel: `${
          e.endTime ? `${formatTimeFromTs(e.endTime)} ${e.endReference ? ' - ' + e.endReference : ''}` : '-'
        }`,
        startPoint: e.startPoint,
        events: e.events?.map((eventAlarm) => ({
          uuid: eventAlarm.uuid,
          timelineType: eventAlarm.timelineType,
          startTime: eventAlarm.startTime,
          endTime: eventAlarm.endTime,
          startLabel: `${eventAlarm.alarmType ? getAlarmTypeName(eventAlarm.alarmType) : ''}
            - ${eventAlarm.speed ?? 0} km/h ${formatDateFromTs(eventAlarm.startTime)}`,
          startPoint: eventAlarm.startPoint,
          onClick: () => {
            const point = eventAlarm.startPoint.coordinates;
            zoomTo({
              lng: point[0],
              lat: point[1],
            });
          },
          iconName: 'warning',
          backgroundColor: eventAlarm.alarmType ? getAlarmColor(eventAlarm.alarmType) : undefined,
          isHover: false,
        })),
        onHover: (isHover: boolean) => {
          setSelectedHoverPath(isHover ? e.uuid : undefined);
        },
        lineStringCoordinates: e.encodedPolyline
          ? decode(e.encodedPolyline).map((point) => {
              return [point[1], point[0]];
            })
          : undefined,
        iconName: 'polyline',
        distance: e.distance,
        speed: e.speed,
        duration: e.duration,
      };
    });
    setEvents(timeline);
  };

  useEffect(() => {
    if (!eventId) return;
    if (!loadedAlarmTypes) return;

    const loadEvent = async () => {
      startLoading();
      const { timeLineEvents, error } = await getEventTimeline(eventId);
      if (timeLineEvents && !error) {
        getTimeLineByEvents(timeLineEvents);
      } else {
        setEvents(undefined);
      }
      stopLoading();
    };

    loadEvent();
  }, [eventId, update, loadedAlarmTypes]);

  const reload = () => {
    setUpdate((prev) => prev + 1);
  };

  const eventTypeOptions = [
    {
      id: undefined,
      label: 'Todos',
    },
    ...Object.keys(EVENT_ALARM_TYPE_LIST).map((key) => ({
      id: key,
      label: EVENT_ALARM_TYPE_LIST[key],
    })),
  ];

  const onEventTypeChange = (params: OnChangeParams) => {
    params.option?.id ? setEventTypeSelected(params.option.id as TimeLineType) : setEventTypeSelected(undefined);
  };

  const clearFilters = () => {
    resetEventTypeSelected();
  };

  return {
    events,
    eventsTimeline,
    loadingEvent: loading,
    reloadEvent: reload,
    eventTypeSelected,
    eventTypeOptions,
    onEventTypeChange,
    clearFilters,
  };
}
